import { createFileRoute } from '@tanstack/react-router';
import { Link } from '@tanstack/react-router';
import { ContentHeader, ContentSection, FeatureShowcase, FAQSection, CTASection } from "@/components/content";

export const Route = createFileRoute('/concepts/workflow-automation')({
  component: WorkflowAutomationPage,
  head: () => ({
    meta: [
      { title: 'Workflow Automation | Matricks' },
      { name: 'description', content: 'Learn about workflow automation, its benefits, and how to implement automated business processes. Guide to streamlining operations with automation.' },
    ],
  }),
});

const benefits = [
  {
    iconName: "Zap",
    title: "Increased Efficiency",
    description: "Eliminate manual tasks and reduce processing time from hours to seconds.",
  },
  {
    iconName: "Clock",
    title: "24/7 Operations",
    description: "Automated workflows run around the clock without human intervention.",
  },
  {
    iconName: "Users",
    title: "Reduced Errors",
    description: "Remove human error from repetitive processes with consistent execution.",
  },
  {
    iconName: "Bot",
    title: "Scalability",
    description: "Handle increasing workloads without proportionally increasing staff.",
  },
  {
    iconName: "BarChart3",
    title: "Better Visibility",
    description: "Track every step of your processes with detailed logs and analytics.",
  },
  {
    iconName: "Workflow",
    title: "Faster Response",
    description: "React to events in real-time with trigger-based automation.",
  },
];

const types = [
  {
    title: "Sequential Workflows",
    description: "Steps execute one after another in a defined order. Simple to understand and debug.",
    example: "Order processing: validate → charge payment → update inventory → send confirmation",
  },
  {
    title: "Parallel Workflows",
    description: "Multiple steps execute simultaneously, then converge. Faster for independent tasks.",
    example: "User onboarding: send welcome email AND create account AND provision resources",
  },
  {
    title: "Conditional Workflows",
    description: "Different paths based on conditions or data. Handles complex business logic.",
    example: "Approval routing: if amount > $10k, require VP approval; else, manager approval",
  },
  {
    title: "Event-Driven Workflows",
    description: "Triggered by external events like webhooks, database changes, or schedules.",
    example: "When customer signs up → create CRM record → assign to sales rep → schedule call",
  },
];

const faqItems = [
  {
    question: "What's the difference between workflow automation and RPA?",
    answer: "Workflow automation orchestrates tasks across systems using APIs and integrations. RPA (Robotic Process Automation) mimics human interactions with application UIs. Workflow automation is generally more reliable and maintainable, while RPA is useful for legacy systems without APIs.",
  },
  {
    question: "How do I identify processes to automate?",
    answer: "Look for processes that are repetitive, rule-based, high-volume, time-sensitive, or prone to errors. Start with simple processes to build confidence, then tackle more complex workflows. Avoid automating processes that require significant human judgment.",
  },
  {
    question: "What triggers can start a workflow?",
    answer: "Common triggers include: webhooks from other applications, scheduled times (cron), database changes, file uploads, form submissions, email receipt, API calls, and manual triggers. The best automation platforms support multiple trigger types.",
  },
  {
    question: "How do I handle errors in automated workflows?",
    answer: "Implement error handling at multiple levels: retry failed steps with exponential backoff, send alerts when retries exhaust, provide fallback paths for critical failures, and log everything for debugging. Design workflows assuming failures will occur.",
  },
  {
    question: "Can workflows include human approval steps?",
    answer: "Yes, human-in-the-loop automation is common. Workflows can pause and wait for human approval, then continue automatically. This balances automation efficiency with necessary human oversight for critical decisions.",
  },
];

function WorkflowAutomationPage() {
  return (
    <div>
      <ContentHeader
        title="Workflow Automation"
        description="Workflow automation is the design, execution, and automation of business processes where tasks, information, or documents are passed between participants according to defined rules."
        breadcrumbs={[
          { label: "Home", href: "/" },
          { label: "Concepts", href: "/concepts" },
          { label: "Workflow Automation" },
        ]}
        badge="Concept"
      />

      {/* What is Workflow Automation */}
      <ContentSection id="what-is-workflow-automation" title="Understanding Workflow Automation">
        <div className="prose prose-gray dark:prose-invert max-w-none">
          <p className="text-lg text-muted-foreground leading-relaxed">
            Every organization has processes—sequences of tasks that need to happen in a specific order
            to accomplish a goal. Workflow automation takes these manual processes and executes them
            automatically, reducing human effort and increasing consistency.
          </p>

          <p className="text-muted-foreground leading-relaxed mt-4">
            Modern workflow automation goes beyond simple task sequences. It can react to events in
            real-time, make decisions based on data, integrate with dozens of systems, and even
            incorporate AI for intelligent routing and processing.
          </p>
        </div>
      </ContentSection>

      {/* Benefits */}
      <ContentSection
        id="benefits"
        title="Key Benefits"
        description="Workflow automation delivers significant advantages for organizations of all sizes."
        background="muted"
      >
        <FeatureShowcase features={benefits} columns={3} variant="cards" />
      </ContentSection>

      {/* Types */}
      <ContentSection
        id="types"
        title="Types of Workflows"
        description="Different patterns for different automation needs."
      >
        <div className="space-y-6">
          {types.map((type) => (
            <div key={type.title} className="rounded-xl border bg-card p-6">
              <h3 className="font-semibold text-lg">{type.title}</h3>
              <p className="text-muted-foreground mt-2">{type.description}</p>
              <div className="mt-4 rounded-lg bg-muted/50 p-4">
                <p className="text-sm"><span className="font-medium">Example:</span> {type.example}</p>
              </div>
            </div>
          ))}
        </div>
      </ContentSection>

      {/* Building Blocks */}
      <ContentSection
        id="building-blocks"
        title="Workflow Building Blocks"
        background="muted"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Triggers</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Events that start workflows: webhooks, schedules, database changes, manual buttons.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Actions</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Tasks the workflow performs: API calls, data transformations, notifications, file operations.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Conditions</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Logic that determines flow: if/else branches, switches, filters, validation rules.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Loops</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Repeat actions for collections: iterate over lists, batch processing, pagination handling.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Variables</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Data passed between steps: input data, transformed values, API responses, accumulated results.
            </p>
          </div>
          <div className="rounded-xl border bg-card p-6">
            <h3 className="font-semibold">Error Handling</h3>
            <p className="text-sm text-muted-foreground mt-2">
              Manage failures gracefully: retries, fallbacks, notifications, logging, dead letter queues.
            </p>
          </div>
        </div>
      </ContentSection>

      {/* Related Concepts */}
      <ContentSection id="related" title="Related Concepts">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Link
            to="/concepts/webhooks"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Webhooks</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Event-driven triggers for workflows
            </p>
          </Link>
          <Link
            to="/concepts/ai-agents"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">AI Agents</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Intelligent automation with AI
            </p>
          </Link>
          <Link
            to="/features/workflow-engine"
            className="rounded-lg border bg-card p-4 hover:border-primary/50 transition-colors"
          >
            <h4 className="font-medium">Workflow Builder</h4>
            <p className="text-sm text-muted-foreground mt-1">
              Visual workflow creation
            </p>
          </Link>
        </div>
      </ContentSection>

      {/* FAQ */}
      <FAQSection items={faqItems} />

      {/* CTA */}
      <CTASection
        title="Automate Your Workflows"
        description="Build powerful automated workflows with Matricks' visual builder. Connect to 2000+ services and add AI intelligence to your processes."
        primaryAction={{ label: "Start Building", href: "/login" }}
        secondaryAction={{ label: "View Workflow Builder", href: "/features/workflow-engine" }}
        background="primary"
      />
    </div>
  );
}
